/**
 * 时钟校准（§7.8）+ 倒计时插值（§11.5 / 红线 #48）。
 *
 * 服务端下发的是**绝对时间戳**（`displayDeadlineAt` / `hardDeadlineAt`，服务器时钟），
 * 而手机的本地时钟可能差几秒到几分钟（用户手动改过、NTP 刚校时、模拟器）。
 * 直接 `deadline - Date.now()` 会算出「还剩 -37 秒」或者「还剩 3 分钟」。
 *
 * 所以：
 *   rtt    = (t3 - t0) - (t2 - t1)          ★ 减去服务端停留时间，不是加
 *   offset = ((t1 - t0) + (t2 - t3)) / 2    ★ serverClock - clientClock
 *
 * 采样策略（§7.8）：保留最近 8 个样本，**取 rtt 最小那个样本的 offset**。
 * 不用中位数：移动网络的延迟分布是长尾且不对称的，中位数样本里已经掺了 100~400ms
 * 的单程排队延迟，而最小 RTT 的那次最接近「没有排队」的真值。
 *
 * ★ 红线 #48：倒计时不走 React state。`createCountdown` 每帧现算剩余毫秒并交给
 *   一个直接写 DOM 的回调（`el.textContent` / `style.strokeDashoffset`），
 *   整个倒计时期间零 setState、零 re-render。
 *   顺带解决后台节流：rAF 在后台停了，数字停住；回前台第一帧现算，自动跳到正确值，
 *   不需要任何「补偿已流逝时间」的逻辑。
 */
import type { Millis } from '@poker/shared'

/** 保留的样本数（§7.8） */
export const CLOCK_SAMPLE_WINDOW = 8

/** 偏移跳变超过这个量，视为系统校时/用户改表：直接 snap 并请求一次快照（§7.8） */
export const CLOCK_SNAP_THRESHOLD_MS = 5_000

export interface ClockSample {
  readonly rttMs: number
  /** serverClock - clientClock */
  readonly offsetMs: number
  /** 本地时钟的采样时刻，仅用于调试 */
  readonly atMs: number
}

export interface ClockUpdate {
  readonly sample: ClockSample
  /** 生效后的偏移 */
  readonly offsetMs: number
  /**
   * true = 偏移发生了 > 5s 的跳变。
   * 调用方必须据此触发一次 `requestSnapshotOnce('desync')`：
   * 时钟跳了说明这段时间的倒计时全是错的，界面很可能也已经和服务端不一致。
   */
  readonly snapped: boolean
}

/** §7.8 的两个公式。纯函数，方便被单测钉死——这两行历史上被写错过两次。 */
export function computeClockSample(t0: Millis, t1: Millis, t2: Millis, t3: Millis): { rttMs: number; offsetMs: number } {
  return {
    rttMs: t3 - t0 - (t2 - t1),
    offsetMs: (t1 - t0 + (t2 - t3)) / 2,
  }
}

/** 从样本窗口里挑 offset：取 rtt 最小的那个（不是中位数，见文件头）。 */
export function pickOffset(samples: readonly ClockSample[]): number {
  let best: ClockSample | null = null
  for (const s of samples) {
    if (best === null || s.rttMs < best.rttMs) best = s
  }
  return best === null ? 0 : best.offsetMs
}

export interface ServerClock {
  /** 收到 `pong{nonce,t0,t1,t2}` 时调用，t3 为本地收到的时刻 */
  addSample(t0: Millis, t1: Millis, t2: Millis, t3: Millis): ClockUpdate
  /** serverClock - clientClock；无样本时为 0（退化成直接用本地时钟） */
  offsetMs(): number
  /** 最近一次采样的 rtt，UI 的网络指示灯用 */
  rttMs(): number
  /** 估计的服务器当前时间 */
  now(): Millis
  /** 距离某个服务端绝对时间戳还剩多少毫秒；null 或已过期返回 0 */
  msUntil(deadlineAt: Millis | null | undefined): number
  sampleCount(): number
  samples(): readonly ClockSample[]
  /** epoch 变化（服务端换进程）时调用：旧样本的参照系已经没了 */
  reset(): void
}

export interface ServerClockOptions {
  readonly now?: () => Millis
  readonly window?: number
  readonly snapThresholdMs?: number
}

export function createServerClock(options: ServerClockOptions = {}): ServerClock {
  const nowFn = options.now ?? (() => Date.now())
  const window = options.window ?? CLOCK_SAMPLE_WINDOW
  const snapThreshold = options.snapThresholdMs ?? CLOCK_SNAP_THRESHOLD_MS

  let samples: ClockSample[] = []
  let offset = 0
  let rtt = 0

  return {
    addSample(t0, t1, t2, t3) {
      const { rttMs, offsetMs } = computeClockSample(t0, t1, t2, t3)
      const sample: ClockSample = { rttMs, offsetMs, atMs: t3 }
      samples.push(sample)
      if (samples.length > window) samples = samples.slice(samples.length - window)
      rtt = rttMs

      // 跳变判定比的是**这一条新样本**与当前偏移：窗口里的旧样本是按旧参照系算的，
      // 拿 pickOffset 的结果去比会被它们压住，系统校时后永远检测不到跳变。
      const had = samples.length > 1
      const snapped = had && Math.abs(offsetMs - offset) > snapThreshold
      const next = pickOffset(samples)
      if (snapped) {
        // 时钟跳了：旧样本是按旧参照系算的，留着只会把新偏移拖回去
        samples = [sample]
        offset = offsetMs
      } else {
        offset = next
      }
      return { sample, offsetMs: offset, snapped }
    },
    offsetMs: () => offset,
    rttMs: () => rtt,
    now: () => nowFn() + offset,
    msUntil(deadlineAt) {
      if (deadlineAt === null || deadlineAt === undefined) return 0
      return Math.max(0, deadlineAt - (nowFn() + offset))
    },
    sampleCount: () => samples.length,
    samples: () => samples,
    reset() {
      samples = []
      offset = 0
      rtt = 0
    },
  }
}

// ============================================================================
// 倒计时（红线 #48：不走 React state）
// ============================================================================

/**
 * 每帧回调。**实现里只允许直接写 DOM**（ref.current.textContent / style），
 * 绝不允许 setState —— 25 秒的倒计时按 60fps 算就是 1500 次整树 re-render。
 *
 * @param remainingMs 距 displayDeadlineAt 的剩余毫秒（已 clamp 到 >= 0）
 * @param fraction    剩余比例 0..1（没给 totalMs 时恒为 0，进度环按需自己算）
 */
export type CountdownSink = (remainingMs: number, fraction: number) => void

export interface Countdown {
  /**
   * 换一个截止时间。`deadlineAt === null` 表示没有倒计时（停表并回调一次 0）。
   * @param totalMs 进度环的分母，一般用 `ActionPrompt.baseTimeoutMs`
   */
  setDeadline(deadlineAt: Millis | null, totalMs?: number): void
  /** 立刻停表，不回调 */
  stop(): void
  running(): boolean
}

export interface CountdownEnv {
  requestAnimationFrame(cb: (t: number) => void): number
  cancelAnimationFrame(handle: number): void
}

/** node / 测试环境没有 rAF：退化成 ~30fps 的 setTimeout，语义完全一样。 */
export function defaultCountdownEnv(): CountdownEnv {
  const g = globalThis as unknown as Partial<CountdownEnv>
  if (typeof g.requestAnimationFrame === 'function' && typeof g.cancelAnimationFrame === 'function') {
    return {
      requestAnimationFrame: g.requestAnimationFrame.bind(globalThis),
      cancelAnimationFrame: g.cancelAnimationFrame.bind(globalThis),
    }
  }
  return {
    requestAnimationFrame: (cb) => globalThis.setTimeout(() => cb(Date.now()), 33) as unknown as number,
    cancelAnimationFrame: (h) => {
      globalThis.clearTimeout(h as unknown as ReturnType<typeof setTimeout>)
    },
  }
}

export function createCountdown(clock: ServerClock, sink: CountdownSink, env: CountdownEnv = defaultCountdownEnv()): Countdown {
  let handle: number | null = null
  let deadline: Millis | null = null
  let total = 0

  const cancel = (): void => {
    if (handle !== null) {
      env.cancelAnimationFrame(handle)
      handle = null
    }
  }

  const tick = (): void => {
    handle = null
    if (deadline === null) return
    // 每帧**现算**，不做累减：累减会在后台节流后一路偏，而现算天然自愈
    const remaining = clock.msUntil(deadline)
    sink(remaining, total > 0 ? Math.min(1, remaining / total) : 0)
    if (remaining <= 0) {
      deadline = null
      return
    }
    handle = env.requestAnimationFrame(tick)
  }

  return {
    setDeadline(next, totalMs = 0) {
      cancel()
      deadline = next
      total = totalMs
      if (next === null) {
        sink(0, 0)
        return
      }
      tick()
    },
    stop() {
      cancel()
      deadline = null
    },
    running: () => deadline !== null,
  }
}
